// Kit Order Service for handling art kit orders and fulfillment
// Direct-to-shop order system with Square payment links
// Uses Netlify Blobs for storage (works from browser AND serverless functions)
import { blobOrderStorage } from './blobOrderStorage'
import { customerService } from './customerService'
import { squarePaymentService } from './squarePaymentService'

export interface KitOrder {
  id: string
  kitId: string
  kitName: string
  kitPrice: number

  // Customer Info
  customerId?: string // Linked customer ID if exists
  customerName: string
  customerEmail: string
  customerPhone: string

  // Order Details
  quantity: number
  totalPrice: number
  orderDate: string
  status: 'pending' | 'fulfilled' | 'cancelled'
  fulfillmentDate?: string
  cancellationDate?: string

  // Payment Info
  paymentLinkId?: string
  paymentLinkUrl?: string
  paymentStatus?: 'unpaid' | 'paid' | 'refunded'
  paymentId?: string

  // Additional Info
  notes?: string
  preferredPickup?: boolean
  deliveryAddress?: string
  paymentMethod?: string

  // Metadata
  lastUpdated: string
}

export interface KitOrderData {
  kitId: string
  kitName: string
  kitPrice: number
  customerName: string
  customerEmail: string
  customerPhone: string
  quantity: number
  notes?: string
  preferredPickup?: boolean
  deliveryAddress?: string
  paymentMethod?: string
}

class KitOrderService {
  /**
   * Generate a unique order ID
   */
  private generateOrderId(): string {
    return `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Get all orders from blob storage
   */
  async getOrders(): Promise<KitOrder[]> {
    return await blobOrderStorage.getOrders()
  }

  /**
   * Get orders by status
   */
  async getOrdersByStatus(status: 'pending' | 'fulfilled' | 'cancelled'): Promise<KitOrder[]> {
    const orders = await this.getOrders()
    return orders.filter(order => order.status === status)
  }

  /**
   * Get pending orders count
   */
  async getPendingOrdersCount(): Promise<number> {
    const pending = await this.getOrdersByStatus('pending')
    return pending.length
  }

  /**
   * Get orders for a specific customer email
   */
  async getOrdersByCustomer(email: string): Promise<KitOrder[]> {
    const orders = await this.getOrders()
    return orders.filter(order =>
      order.customerEmail.toLowerCase() === email.toLowerCase()
    )
  }

  /**
   * Find or create customer for order
   */
  private async findOrCreateCustomer(orderData: KitOrderData): Promise<string | undefined> {
    try {
      // Check if customer exists
      const existingCustomers = customerService.getCustomers()
      const existingCustomer = existingCustomers.find(
        c => c.email.toLowerCase() === orderData.customerEmail.toLowerCase()
      )

      if (existingCustomer) {
        console.log('✅ Found existing customer:', existingCustomer.email)
        return existingCustomer.id
      }

      // Create new customer
      console.log('📝 Creating new customer for order:', orderData.customerEmail)
      const newCustomer = await customerService.registerCustomer({
        name: orderData.customerName,
        email: orderData.customerEmail,
        phone: orderData.customerPhone,
        address: orderData.deliveryAddress,
        preferences: {
          emailNotifications: true,
          smsNotifications: false,
          preferredClassTypes: [],
          notes: 'Auto-created from kit order'
        }
      })

      console.log('✅ Customer created:', newCustomer.id)
      return newCustomer.id
    } catch (error) {
      console.error('⚠️ Error creating customer, order will proceed without customer ID:', error)
      return undefined
    }
  }

  /**
   * Create a new kit order
   */
  async createOrder(orderData: KitOrderData): Promise<KitOrder> {
    try {
      // Find or create customer
      const customerId = await this.findOrCreateCustomer(orderData)

      // Calculate total price
      const totalPrice = orderData.kitPrice * orderData.quantity

      // Generate order ID first
      const orderId = this.generateOrderId()

      // Create new order
      const newOrder: KitOrder = {
        id: orderId,
        kitId: orderData.kitId,
        kitName: orderData.kitName,
        kitPrice: orderData.kitPrice,
        customerId,
        customerName: orderData.customerName,
        customerEmail: orderData.customerEmail,
        customerPhone: orderData.customerPhone,
        quantity: orderData.quantity,
        totalPrice,
        orderDate: new Date().toISOString(),
        status: 'pending',
        paymentStatus: 'unpaid',
        paymentMethod: orderData.paymentMethod,
        notes: orderData.notes,
        preferredPickup: orderData.paymentMethod,
        deliveryAddress: orderData.deliveryAddress,
        lastUpdated: new Date().toISOString()
      }

      // Create Square payment link only for credit card payments
      try {
        if (orderData.paymentMethod === 'credit-card' && squarePaymentService.isConfigured()) {
          console.log('📧 Creating Square payment link for order:', orderId)
          const paymentLink = await squarePaymentService.createPaymentLink({
            orderId: orderId,
            orderDescription: `${orderData.kitName} (Qty: ${orderData.quantity})`,
            amount: totalPrice,
            customerEmail: orderData.customerEmail,
            customerName: orderData.customerName
          })

          newOrder.paymentLinkId = paymentLink.id
          newOrder.paymentLinkUrl = paymentLink.url
          console.log('✅ Square payment link created:', paymentLink.url)
        } else if (orderData.paymentMethod === 'credit-card') {
          console.log('⚠️ Square not configured, order created without payment link')
        } else {
          console.log(`ℹ️ Payment method is ${orderData.paymentMethod}, no Square payment link needed`)
        }
      } catch (paymentError) {
        console.warn('⚠️ Failed to create payment link, order will proceed without it:', paymentError)
        // Don't fail the order if payment link creation fails
      }

      // Save order to blob storage
      await blobOrderStorage.addOrder(newOrder)

      console.log('✅ Kit order created successfully:', newOrder.id)
      return newOrder
    } catch (error) {
      console.error('❌ Error creating kit order:', error)
      throw new Error(`Failed to create order: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Update order status
   */
  async updateOrderStatus(
    orderId: string,
    status: 'pending' | 'fulfilled' | 'cancelled'
  ): Promise<KitOrder> {
    try {
      const updates: Partial<KitOrder> = {
        status,
        lastUpdated: new Date().toISOString()
      }

      if (status === 'fulfilled') {
        updates.fulfillmentDate = new Date().toISOString()
      } else if (status === 'cancelled') {
        updates.cancellationDate = new Date().toISOString()
      }

      const updatedOrder = await blobOrderStorage.updateOrder(orderId, updates)

      if (!updatedOrder) {
        throw new Error('Order not found')
      }

      console.log('✅ Order status updated:', orderId, '→', status)
      return updatedOrder
    } catch (error) {
      console.error('❌ Error updating order status:', error)
      throw new Error(`Failed to update order: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Update order payment status (called by webhook)
   */
  async updatePaymentStatus(
    orderId: string,
    paymentStatus: 'unpaid' | 'paid' | 'refunded',
    paymentId?: string
  ): Promise<KitOrder> {
    try {
      const updates: Partial<KitOrder> = {
        paymentStatus,
        paymentId,
        lastUpdated: new Date().toISOString()
      }

      const updatedOrder = await blobOrderStorage.updateOrder(orderId, updates)

      if (!updatedOrder) {
        throw new Error('Order not found')
      }

      console.log('✅ Payment status updated:', orderId, '→', paymentStatus)
      return updatedOrder
    } catch (error) {
      console.error('❌ Error updating payment status:', error)
      throw new Error(`Failed to update payment status: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Delete an order
   */
  async deleteOrder(orderId: string): Promise<void> {
    try {
      await blobOrderStorage.deleteOrder(orderId)
      console.log('✅ Order deleted successfully:', orderId)
    } catch (error) {
      console.error('❌ Error deleting order:', error)
      throw new Error(`Failed to delete order: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Get order by ID
   */
  async getOrderById(orderId: string): Promise<KitOrder | null> {
    return await blobOrderStorage.getOrderById(orderId)
  }

  /**
   * Get orders from this month
   */
  async getOrdersThisMonth(): Promise<KitOrder[]> {
    const now = new Date()
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const orders = await this.getOrders()

    return orders.filter(order => {
      const orderDate = new Date(order.orderDate)
      return orderDate >= firstDayOfMonth
    })
  }

  /**
   * Get total revenue from fulfilled orders
   */
  async getTotalRevenue(): Promise<number> {
    const fulfilled = await this.getOrdersByStatus('fulfilled')
    return fulfilled.reduce((sum, order) => sum + order.totalPrice, 0)
  }

  /**
   * Get statistics for dashboard
   */
  async getStats() {
    const allOrders = await this.getOrders()
    const pending = allOrders.filter(o => o.status === 'pending').length
    const fulfilled = allOrders.filter(o => o.status === 'fulfilled').length
    const cancelled = allOrders.filter(o => o.status === 'cancelled').length
    const thisMonth = (await this.getOrdersThisMonth()).length
    const revenue = await this.getTotalRevenue()

    return {
      total: allOrders.length,
      pending,
      fulfilled,
      cancelled,
      thisMonth,
      totalRevenue: revenue
    }
  }
}

// Export singleton instance
export const kitOrderService = new KitOrderService()
